import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Timer, Landmark, Info, ArrowRight } from "lucide-react";
import CONFIG from "../../config";

const DEFAULT_CHARGES = [
  { condition: "before_accept", label: "Before partner accepts",             percent: 5 },
  { condition: "after_accept",  label: "After partner accepts, before work", percent: 10 },
  { condition: "after_start",   label: "After work has started",             percent: 15 },
];

const RefundPolicy = () => {
  const navigate = useNavigate();
  const [charges, setCharges] = useState(DEFAULT_CHARGES);

  useEffect(() => {
    let cancelled = false;
    fetch(`${CONFIG.API_BASE_URL}/api/order/config/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (Array.isArray(data.charges) && data.charges.length) setCharges(data.charges);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Refund Policy</div>

        <h1 style={styles.title}>Refund Policy</h1>
        <p style={styles.intro}>
          If you cancel a paid booking, the amount you already paid is refunded back to
          you minus the applicable cancellation charge (if any). Below is how refunds are
          calculated and processed today — we'll be adding the complete, detailed refund
          policy here shortly.
        </p>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>What You Get Back</h2>
          <div style={styles.tierGrid}>
            {charges.map((tier) => (
              <div key={tier.condition || tier.label} style={styles.tierCard}>
                <div style={styles.tierRefundPct}>{100 - Number(tier.percent || 0)}%</div>
                <div style={styles.tierNote}>refunded</div>
                <div style={styles.tierLabel}>{tier.label}</div>
                <div style={styles.tierSub}>({tier.percent}% cancellation charge applies)</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>How Refunds Are Processed</h2>
          <div style={styles.stepsRow}>
            <div style={styles.stepItem}>
              <div style={styles.stepIcon}><Wallet size={18} color="#6d5efc" /></div>
              <div style={styles.stepTitle}>Refund Initiated</div>
              <div style={styles.stepDesc}>As soon as your cancellation is confirmed, a refund is initiated for the eligible amount.</div>
            </div>
            <div style={styles.stepItem}>
              <div style={styles.stepIcon}><Landmark size={18} color="#6d5efc" /></div>
              <div style={styles.stepTitle}>Original Payment Method</div>
              <div style={styles.stepDesc}>Refunds go back to the card, UPI, or wallet you originally paid with — we don't issue cash refunds.</div>
            </div>
            <div style={styles.stepItem}>
              <div style={styles.stepIcon}><Timer size={18} color="#6d5efc" /></div>
              <div style={styles.stepTitle}>5–7 Business Days</div>
              <div style={styles.stepDesc}>Typical time for the refund to reflect, depending on your bank or payment provider.</div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Good to Know</h2>
          <ul style={styles.list}>
            <li>For "Pay After Work" bookings, no payment was collected upfront — so if a cancellation charge applies, it is payable separately; there is nothing to refund.</li>
            <li>Once a service is marked completed, the booking is no longer eligible for cancellation or refund.</li>
            <li>You can track the status of an order, including any cancellation, from your <button style={styles.inlineLink} onClick={() => navigate("/user/user-dashboard")}>My Bookings<ArrowRight size={12} /></button> page.</li>
          </ul>
        </div>

        <div style={styles.noteBar}>
          <Info size={16} color="#6d5efc" />
          <span>
            This page currently reflects our cancellation-based refund calculation. A
            complete, detailed refund policy (covering more scenarios) will be published
            here soon.
          </span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: {
    maxWidth: "860px",
    margin: "0 auto",
  },
  breadcrumb: {
    color: "#7b8198",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "14px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    fontWeight: 800,
    color: "#1c2148",
    marginBottom: "12px",
  },
  intro: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.75,
    color: "#5c6480",
    marginBottom: "28px",
    maxWidth: "700px",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "20px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 800,
    color: "#21284d",
    marginBottom: "18px",
  },
  tierGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  tierCard: {
    background: "#f8f8fe",
    borderRadius: "16px",
    padding: "18px",
    textAlign: "center",
  },
  tierRefundPct: {
    fontSize: "26px",
    fontWeight: 800,
    color: "#1d2448",
  },
  tierNote: {
    fontSize: "12px",
    color: "#8791ac",
    marginBottom: "8px",
  },
  tierLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#3c4462",
    marginBottom: "4px",
  },
  tierSub: {
    fontSize: "11.5px",
    color: "#9ca3af",
  },
  stepsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "18px",
  },
  stepItem: {
    textAlign: "center",
  },
  stepIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    background: "#f0eeff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
  },
  stepTitle: {
    fontSize: "14px",
    fontWeight: 800,
    color: "#263053",
    marginBottom: "6px",
  },
  stepDesc: {
    fontSize: "12.5px",
    lineHeight: 1.6,
    color: "#707893",
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "#5c6480",
    fontSize: "14px",
    lineHeight: 1.9,
  },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: 0,
  },
  noteBar: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    background: "#f0eeff",
    borderRadius: "14px",
    padding: "16px 18px",
    fontSize: "13px",
    color: "#4a4470",
    lineHeight: 1.6,
  },
};

export default RefundPolicy;
